import { useMemo } from "react";
import { addMonths } from "date-fns";
import { expandOccurrences } from "@api";
import { itemAnchorDate } from "@areas/calendar/common";
import { useCalendarItems, useCalendars } from "@areas/calendar/hooks/useCalendar";

const HORIZON_MONTHS = 12; // how far ahead to surface scheduled work
const MAX_ITEMS = 50; // cap so a busy recurrence can't flood the rail

// A task that's been done or dropped isn't upcoming work, whatever its due date.
const CLOSED_TASK_STATUSES = new Set(["COMPLETED", "CANCELLED"]);

/**
 * Everything Ontik has scheduled for the signed-in user from now onward — events from the calendar
 * feed, expanded (incl. recurrences) across the horizon, plus open tasks at their due time — sorted
 * soonest first. Tasks carry a DUE rather than a DTSTART, so they anchor on that (the same rule the
 * calendar grid uses). The lower bound is the current instant, not the start of today, so the rail
 * is strictly upcoming: an occurrence that has already finished never shows.
 */
export const useSchedule = () => {
  const { data: calendars } = useCalendars();
  const calendar = calendars?.[0];
  const { data: items = [], isLoading, error } = useCalendarItems(calendar?.href);

  const events = useMemo(() => {
    const now = new Date();
    const end = addMonths(now, HORIZON_MONTHS);
    return items
      .filter((item) => !(item.kind === "task" && CLOSED_TASK_STATUSES.has(item.status)))
      .flatMap((item) => {
        if (item.start) return expandOccurrences(item, now, end);
        // Due-only (a task): a single occurrence at its due time, when that's still ahead.
        if (item.due && item.due >= now && item.due < end) return [{ ...item, occurrenceKey: item.uid }];
        return [];
      })
      .sort((a, b) => (itemAnchorDate(a)?.getTime() ?? 0) - (itemAnchorDate(b)?.getTime() ?? 0))
      .slice(0, MAX_ITEMS);
  }, [items]);

  return { events, isLoading: !!calendar && isLoading, error };
};
